import { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "./components/ui/sonner";
import axios from "axios";
import { useNotificationWebSocket } from "./hooks/useNotificationWebSocket";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Transfer from "./pages/Transfer";
import Exchange from "./pages/Exchange";
import Grow from "./pages/Grow";
import Explore from "./pages/Explore";
import Lifestyle from "./pages/Lifestyle";
import Transactions from "./pages/Transactions";
import Bills from "./pages/Bills";
import AddBiller from "./pages/AddBiller";
import BillDetail from "./pages/BillDetail";
import DiasporaBills from "./pages/DiasporaBills";
import Receive from "./pages/Receive";
import ScanPay from "./pages/ScanPay";
import VirtualCard from "./pages/VirtualCard";
import SpendingLimits from "./pages/SpendingLimits";
import CardSettings from "./pages/CardSettings";
import Remittance from "./pages/Remittance";
import Coach from "./pages/Coach";
import Settings from "./pages/Settings";
import NotificationSettings from "./pages/NotificationSettings";
import Notifications from "./pages/Notifications";
import SplitBill from "./pages/SplitBill";
import KYC from "./pages/KYC";
import SMSSettings from "./pages/SMSSettings";
import PushSettings from "./pages/PushSettings";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import MyTickets from "./pages/MyTickets";
import PromoterDashboard from "./pages/PromoterDashboard";
import CreateEvent from "./pages/CreateEvent";
import ManageEvent from "./pages/ManageEvent";
import AdminDashboard from "./pages/AdminDashboard";
import Referral from "./pages/Referral";
import Premium from "./pages/Premium";
import RecurringPayments from "./pages/RecurringPayments";
import Analytics from "./pages/Analytics";
import MerchantQR from "./pages/MerchantQR";
import PartnerApply from "./pages/PartnerApply";
import StelxContacts from "./pages/StelxContacts";
import BankAccounts from "./pages/BankAccounts";
import AuthCallback from "./pages/AuthCallback";

// Google OAuth Config
const GOOGLE_CLIENT_ID = "510083081801-1map2vlsp9cakpcmkeht2b47q4b5d1dv.apps.googleusercontent.com";

// Components
import OnboardingModal from "./components/OnboardingModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Export Google OAuth URL builder for use in components
export const getGoogleAuthUrl = () => {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const scope = "openid email profile";
  return `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 1,
    },
  },
});

// Protected Route Component with WebSocket Notifications
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Handle new notification
  const handleNewNotification = useCallback((notification) => {
    console.log('New notification:', notification);
    
    // Invalidate notification queries to refresh data
    queryClient.invalidateQueries(['notifications']);
    queryClient.invalidateQueries(['notificationCount']);
    
    // Show toast notification
    const icons = {
      transaction: '💸',
      security: '🔒',
      promotion: '🎁',
      default: '🔔'
    };
    
    toast(notification.title, {
      description: notification.message,
      icon: icons[notification.type] || icons.default,
      duration: 5000,
    });
  }, [queryClient]);

  // Handle unread count update
  const handleUnreadCountUpdate = useCallback((count) => {
    console.log('Unread count updated:', count);
    // Update React Query cache directly
    queryClient.setQueryData(['notificationCount'], { unread_count: count });
  }, [queryClient]);

  // WebSocket connection
  const { isConnected, unreadCount } = useNotificationWebSocket(
    token,
    handleNewNotification,
    handleUnreadCountUpdate
  );

  useEffect(() => {
    // If user data passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setToken(location.state.user.token);
      setIsAuthenticated(true);
      // Store token for API calls
      if (location.state.user.token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${location.state.user.token}`;
        localStorage.setItem('stelx_token', location.state.user.token);
      }
      return;
    }

    const checkAuth = async () => {
      // Try to get token from localStorage
      const storedToken = localStorage.getItem('stelx_token');
      if (storedToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        setToken(storedToken);
      }
      
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
        if (response.data.token) {
          setToken(response.data.token);
          localStorage.setItem('stelx_token', response.data.token);
        }
      } catch (error) {
        setIsAuthenticated(false);
        localStorage.removeItem('stelx_token');
        delete axios.defaults.headers.common['Authorization'];
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  useEffect(() => {
    // Check if user needs onboarding (first time login)
    if (isAuthenticated && user) {
      const onboardingComplete = localStorage.getItem("stelx_onboarding_complete");
      if (!onboardingComplete) {
        setShowOnboarding(true);
      }
    }
  }, [isAuthenticated, user]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Clone children and pass user prop along with WebSocket connection status
  return (
    <>
      {children({ user, wsConnected: isConnected, wsUnreadCount: unreadCount })}
      <OnboardingModal 
        isOpen={showOnboarding} 
        onComplete={() => setShowOnboarding(false)} 
      />
    </>
  );
};

// App Router with Google OAuth
const AppRouter = () => {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/transfer"
        element={
          <ProtectedRoute>
            {({ user }) => <Transfer user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/exchange"
        element={
          <ProtectedRoute>
            {({ user }) => <Exchange user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/grow"
        element={
          <ProtectedRoute>
            {({ user }) => <Grow user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/explore"
        element={
          <ProtectedRoute>
            {({ user }) => <Explore user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/lifestyle"
        element={
          <ProtectedRoute>
            {({ user }) => <Lifestyle user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/transactions"
        element={
          <ProtectedRoute>
            {({ user }) => <Transactions user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/bills"
        element={
          <ProtectedRoute>
            {({ user }) => <Bills user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/bills/add/:providerId"
        element={
          <ProtectedRoute>
            {({ user }) => <AddBiller user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/bills/add"
        element={
          <ProtectedRoute>
            {({ user }) => <Bills user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/bills/:billerId"
        element={
          <ProtectedRoute>
            {({ user }) => <BillDetail user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/bills/diaspora"
        element={
          <ProtectedRoute>
            {({ user }) => <DiasporaBills user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/receive"
        element={
          <ProtectedRoute>
            {({ user }) => <Receive user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/scan"
        element={
          <ProtectedRoute>
            {({ user }) => <ScanPay user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/card"
        element={
          <ProtectedRoute>
            {({ user }) => <VirtualCard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/card/spending-limits"
        element={
          <ProtectedRoute>
            {({ user }) => <SpendingLimits user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/card/settings"
        element={
          <ProtectedRoute>
            {({ user }) => <CardSettings user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/remittance"
        element={
          <ProtectedRoute>
            {({ user }) => <Remittance user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach"
        element={
          <ProtectedRoute>
            {({ user }) => <Coach user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            {({ user }) => <StelxContacts user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/bank-accounts"
        element={
          <ProtectedRoute>
            {({ user }) => <BankAccounts user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {({ user }) => <Settings user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            {({ user }) => <NotificationSettings user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/kyc"
        element={
          <ProtectedRoute>
            {({ user }) => <KYC user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            {({ user }) => <Notifications user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/split-bill"
        element={
          <ProtectedRoute>
            {({ user }) => <SplitBill user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/sms"
        element={
          <ProtectedRoute>
            {({ user }) => <SMSSettings user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/push"
        element={
          <ProtectedRoute>
            {({ user }) => <PushSettings user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            {({ user }) => <Events user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:eventId"
        element={
          <ProtectedRoute>
            {({ user }) => <EventDetail user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-tickets"
        element={
          <ProtectedRoute>
            {({ user }) => <MyTickets user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/promoter"
        element={
          <ProtectedRoute>
            {({ user }) => <PromoterDashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/promoter/events/create"
        element={
          <ProtectedRoute>
            {({ user }) => <CreateEvent user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/promoter/events/:eventId"
        element={
          <ProtectedRoute>
            {({ user }) => <ManageEvent user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {({ user }) => <AdminDashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/referral"
        element={
          <ProtectedRoute>
            {({ user }) => <Referral user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium"
        element={
          <ProtectedRoute>
            {({ user }) => <Premium user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/recurring"
        element={
          <ProtectedRoute>
            {({ user }) => <RecurringPayments user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            {({ user }) => <Analytics user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/merchant-qr"
        element={
          <ProtectedRoute>
            {({ user }) => <MerchantQR user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/partner/apply"
        element={
          <ProtectedRoute>
            {({ user }) => <PartnerApply user={user} />}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <div className="min-h-screen bg-background">
          <AppRouter />
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
